/**
 * The Run buttons. Copied from `brainstorm/intents.ts` minus CUT — a boardroom
 * has no phase for "rule out the weak" to hang on; ruling out is a verb on an
 * item (the skeptic's critique, or the human's Rule out in the drawer).
 */
import type { BoardroomRunIntent } from '../../types';

export interface RunOption {
  intent: BoardroomRunIntent;
  label: string;
  hint: string;
  className: string;
}

export const RUN_OPTIONS: RunOption[] = [
  {
    intent: 'WIDER',
    label: 'Dig wider',
    hint: 'Untouched ground: new players, new complaints, new ideas.',
    className: 'bg-indigo-600 hover:bg-indigo-700 text-white',
  },
  {
    intent: 'DEEPER',
    label: 'Dig deeper',
    hint: 'What is already on the board: corroborate, read their pages, attack the thin ideas.',
    className: 'bg-teal-600 hover:bg-teal-700 text-white',
  },
];

export const BUDGET_OPTIONS: { tokens: number; label: string }[] = [
  { tokens: 500_000, label: '500k' },
  { tokens: 1_000_000, label: '1M' },
  { tokens: 2_000_000, label: '2M' },
  // THE LONG DIGS. 1M is roughly 24 turns, so these are the sizes where a room
  // is left to work for hours rather than minutes — and the whiteboard is what
  // makes them watchable: a person reads the answer line, not 400 items.
  // Still bounded by the plan's ceiling and by what the balance funds, so a
  // bigger number here offers nothing nobody can pay for.
  { tokens: 10_000_000, label: '10M' },
  { tokens: 20_000_000, label: '20M' },
  { tokens: 30_000_000, label: '30M' },
];

export const DEFAULT_BUDGET = 500_000;

/** THE LAST BUDGET YOU PICKED, per browser. A dig is rarely a one-off — the
 *  same room gets dug again and again at the same size, and re-picking it every
 *  time was a step with no decision in it. Per-viewer and best-effort: a
 *  private window or blocked site data just gets the default back. */
const BUDGET_KEY = 'jcseo_boardroom_budget';

export function rememberedBudget(fallback: number = DEFAULT_BUDGET): number {
  try {
    const n = Number(window.localStorage.getItem(BUDGET_KEY));
    // Only a value the picker still offers: the options change, and a
    // remembered 50k would be a budget with no way back to it.
    return BUDGET_OPTIONS.some(b => b.tokens === n) ? n : fallback;
  } catch {
    return fallback;
  }
}

export function rememberBudget(tokens: number): void {
  try {
    window.localStorage.setItem(BUDGET_KEY, String(tokens));
  } catch {
    /* a viewer who blocks site data simply does not get the convenience */
  }
}

/** The picker's options under a plan's ceiling. The ceiling itself is added
 *  when it falls between two options, so a plan capped at 300k still offers
 *  300k rather than stopping at 200k. Null = no ceiling. */
export function budgetOptionsFor(maxTokens: number | null): { tokens: number; label: string }[] {
  if (maxTokens === null || maxTokens === undefined) return BUDGET_OPTIONS;
  const under = BUDGET_OPTIONS.filter(b => b.tokens <= maxTokens);
  // A ceiling below the smallest option used to return NOTHING — an empty
  // dropdown. It could not happen while the ceiling was the plan's, and it
  // happens the moment the ceiling is what the balance funds: a nearly spent
  // balance buys less than 50k. Offer the ceiling itself; it is a real budget.
  if (!under.length) return [{ tokens: maxTokens, label: budgetLabel(maxTokens) }];
  if (under.some(b => b.tokens === maxTokens)) return under;
  return [...under, { tokens: maxTokens, label: budgetLabel(maxTokens) }];
}

export function budgetLabel(tokens: number): string {
  return tokens >= 1_000_000 ? `${tokens / 1_000_000}M` : `${Math.round(tokens / 1000)}k`;
}

/** The budget to send: the chosen one, clamped to the plan's ceiling. */
export function clampBudget(chosen: number, maxTokens: number | null): number {
  return maxTokens === null || maxTokens === undefined ? chosen : Math.min(chosen, maxTokens);
}

export function endedLabel(reason: string | null): string {
  switch (reason) {
    case 'CREDITS':
      return 'ran out of credits';
    case 'DONE':
      return 'finished';
    case 'BUDGET':
      return 'spent the budget';
    case 'IDLE':
      return 'nothing left to do';
    case 'STOPPED':
      return 'stopped';
    case 'STALE':
      return 'went quiet';
    case 'FAILED':
      return 'failed';
    case 'NO_MODEL':
      return 'stopped: no AI model is set up for it';
    case 'USED_UP':
      return 'found no new players left to find';
    default:
      return reason ? reason.toLowerCase() : '';
  }
}


/** What a finished dig bought, in the fewest words that stay honest.
 *
 *  Closed and opened are kept apart on purpose. A dig that answers twenty-five
 *  of the board's questions and turns up thirty more is the room working well;
 *  one that answers none and turns up five is not — and on a net count both
 *  read as "+5". Returns "" while a run has nothing to report, so the line it
 *  sits in is unchanged for older runs.
 */
export function digResult(run: {
  holes_closed?: number;
  holes_opened?: number;
} | null): string {
  const closed = run?.holes_closed ?? 0;
  const opened = run?.holes_opened ?? 0;
  if (!closed && !opened) return '';
  const parts = [`${closed} answered`];
  if (opened) parts.push(`${opened} new question${opened === 1 ? '' : 's'}`);
  return parts.join(' · ');
}


/** Why the picker is not offering everything, or "".
 *
 *  A CEILING THAT HIDES AN OPTION MUST SAY SO. The credit ceiling had a badge
 *  and the PLAN ceiling had none, so a plan-capped picker simply dropped its
 *  top option with no explanation — the reader is left wondering whether the
 *  build is stale, whether they misread it, or whether the option was removed.
 *  Both ceilings now speak.
 */
export function budgetCapNote(
  maxTokens: number | null | undefined,
  cappedByCredits: boolean,
): string {
  if (maxTokens === null || maxTokens === undefined) return '';
  // Only worth saying when something is actually being withheld.
  if (!BUDGET_OPTIONS.some(b => b.tokens > maxTokens)) return '';
  return cappedByCredits ? 'all your balance buys' : 'your plan’s ceiling';
}
